import { type FunctionComponent } from 'react';
import { I18n } from 'common/i18n';
import optionallyLocalizeUrls from '../../utils/optionally_localize_urls';
import {
  ForgeBadge,
  ForgeButton,
  ForgeCard,
  ForgeDialog,
  ForgeLabelValue,
  ForgeScaffold,
} from '@tylertech/forge-react';
import {
  canCreateStories,
  canCreateMeasures,
  canCreateDatasets,
  canCreateESRIMapLayers,
  canSeeReportingPreview,
} from '../../utils/site_chrome_rights';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import './index.scss';
import getLocalePrefix from 'common/js_utils/getLocalePrefix';

interface CreateAssetsDialogProps {
  open: boolean;
  closeDialogCallback: () => void;
}

const CreateAssetsDialog: FunctionComponent<CreateAssetsDialogProps> = ({ open, closeDialogCallback }) => {
  const scope = 'shared.site_chrome.forge_omnibar.create_assets';

  const label = FeatureFlags.valueOrDefault('enable_tir_domain')
    ? I18n.t(`${scope}.report.label`)
    : I18n.t(`${scope}.story.label`);

  const linkDictionary = {
    dataset: optionallyLocalizeUrls('/datasets/new'),
    story: `/stories${getLocalePrefix()}/new`,
    measure: optionallyLocalizeUrls('/measures/new'),
    esri: '/datasets/new?onlyEsri=true',
  };

  const clickHandler = (url: string) => (window.location.href = url);

  return (
    <ForgeDialog
      open={open}
      on-forge-dialog-close={closeDialogCallback}
      aria-labelledby="create-assets-dialog-title"
    >
      <ForgeScaffold className="create-assets-dialog">
        <h1 id="create-assets-dialog-title" slot="header" className="forge-typography--subheading5">
          {I18n.t(`${scope}.heading`)}
        </h1>
        <div slot="body" className="create-assets-cards">
          {canCreateDatasets() && (
            <ForgeCard raised={true}>
              <ForgeScaffold>
                <div slot="body" className="create-dataset-card-body">
                  <img src="https://cdn.forge.tylertech.com/v1/images/spot/data-table-spot.svg" alt="" />
                  <br />
                  <ForgeLabelValue>
                    <div slot="label" className="forge-typography--heading2">
                      {I18n.t(`${scope}.dataset.label`)}
                    </div>
                    <div slot="value" className="forge-typography--label1">
                      {I18n.t(`${scope}.dataset.description`)}
                    </div>
                  </ForgeLabelValue>
                </div>
                <div className="select-button" slot="footer">
                  <ForgeButton
                    id="forge-create-dataset-button"
                    variant="raised"
                    forge-dialog-focus="true"
                    onClick={() => clickHandler(linkDictionary.dataset)}
                    aria-label={I18n.t(`${scope}.create_dataset`)}
                  >
                    {I18n.t(`${scope}.select`)}
                  </ForgeButton>
                </div>
              </ForgeScaffold>
            </ForgeCard>
          )}
          {canCreateStories() && (
            <ForgeCard raised={true}>
              <ForgeScaffold>
                <div slot="body" className="create-story-card-body">
                  <img src="https://cdn.forge.tylertech.com/v1/images/spot/designer-spot.svg" alt="" />
                  <ForgeLabelValue>
                    <div slot="label" className="forge-typography--heading2">
                      {label}
                    </div>
                    <div slot="value" className="forge-typography--label1">
                      {I18n.t(`${scope}.story.description`)}
                    </div>
                  </ForgeLabelValue>
                </div>
                <div className="select-button" slot="footer">
                  <ForgeButton
                    id="forge-create-story-button"
                    variant="raised"
                    onClick={() => clickHandler(linkDictionary.story)}
                    aria-label={I18n.t(`${scope}.create_story`)}
                  >
                    {I18n.t(`${scope}.select`)}
                  </ForgeButton>
                </div>
              </ForgeScaffold>
            </ForgeCard>
          )}
          {canSeeReportingPreview() && (
            <ForgeCard raised={true}>
              <ForgeScaffold>
                <div slot="body" className="create-report-card-body">
                  <img src="https://cdn.forge.tylertech.com/v1/images/spot/ai-report-spot.svg" />
                  <ForgeLabelValue>
                    <div slot="label" className="ai-report-label">
                      <div className="forge-typography--heading2">{I18n.t(`${scope}.ai_report.label`)}</div>
                      <ForgeBadge theme="info">{I18n.t(`${scope}.ai_report.beta`)}</ForgeBadge>
                    </div>
                    <div slot="value" className="forge-typography--label1">
                      {I18n.t(`${scope}.ai_report.description`)}
                    </div>
                  </ForgeLabelValue>
                </div>
                <ForgeButton
                  className="select-button"
                  slot="footer"
                  id="forge-create-ai-report-button"
                  variant="raised"
                  onClick={() => clickHandler('/reporting')}
                  aria-label={I18n.t(`${scope}.create_report`)}
                >
                  {I18n.t(`${scope}.select`)}
                </ForgeButton>
              </ForgeScaffold>
            </ForgeCard>
          )}
          {canCreateMeasures() && (
            <ForgeCard raised={true}>
              <ForgeScaffold>
                <div slot="body" className="create-measure-card-body">
                  <img src="https://cdn.forge.tylertech.com/v1/images/spot/chart-spot.svg" alt="" />
                  <ForgeLabelValue>
                    <div slot="label" className="forge-typography--heading2">
                      {I18n.t(`${scope}.measure.label`)}
                    </div>
                    <div slot="value" className="forge-typography--label1">
                      {I18n.t(`${scope}.measure.description`)}
                    </div>
                  </ForgeLabelValue>
                </div>
                <div className="select-button" slot="footer">
                  <ForgeButton
                    id="forge-create-measure-button"
                    variant="raised"
                    onClick={() => clickHandler(linkDictionary.measure)}
                    aria-label={I18n.t(`${scope}.create_measure`)}
                  >
                    {I18n.t(`${scope}.select`)}
                  </ForgeButton>
                </div>
              </ForgeScaffold>
            </ForgeCard>
          )}
          {canCreateESRIMapLayers() && (
            <ForgeCard>
              <ForgeScaffold>
                <div slot="body" className="create-esri-layer-card-body">
                  <img src="https://cdn.forge.tylertech.com/v1/images/spot/map-2-spot.svg" alt="" />
                  <ForgeLabelValue>
                    <div slot="label" className="forge-typography--heading2">
                      {I18n.t(`${scope}.esri.label`)}
                    </div>
                    <div slot="value" className="forge-typography--label1">
                      {I18n.t(`${scope}.esri.description`)}
                    </div>
                  </ForgeLabelValue>
                </div>
                <div className="select-button" slot="footer">
                  <ForgeButton
                    id="forge-create-esri-button"
                    variant="raised"
                    onClick={() => clickHandler(linkDictionary.esri)}
                    aria-label={I18n.t(`${scope}.create_esri`)}
                  >
                    {I18n.t(`${scope}.select`)}
                  </ForgeButton>
                </div>
              </ForgeScaffold>
            </ForgeCard>
          )}
        </div>
        <div slot="footer" className="close-button">
          <ForgeButton variant="outlined" onClick={closeDialogCallback}>
            {I18n.t(`${scope}.cancel`)}
          </ForgeButton>
        </div>
      </ForgeScaffold>
    </ForgeDialog>
  );
};

export default CreateAssetsDialog;
